/**
 * The coach's Request a New Time (decision 113; docs/booking-contracts.md
 * 6, proposeLiveLessonTimes): up to three times for one lesson, any time on
 * the server's open-time grid from 6:00 AM to 11:00 PM, any day from today
 * to 6 days after the lesson's original time. A time is greyed, never
 * hidden, when the server would refuse it: past, inside the lead time,
 * after the last day, the lesson's own time, or on another of the coach's
 * lessons. Pure, so it is unit tested.
 */
import { dayKey, windowDays } from './liveCalendar.ts';
import { zonedInstant } from './monthlyCoaching.ts';

const MIN = 60_000;
const DAY = 24 * 60 * MIN;
export const LESSON_TIMES_MAX = 3;
const STEPS = [5, 10, 15, 20, 30];

/** The lesson's original start: its first, before any move (the server's own rule). */
export function originalStart(b: { scheduledStartAt: string; rescheduleHistory?: Array<{ fromStartAt?: string }> | null }): string {
  return b.rescheduleHistory?.[0]?.fromStartAt ?? b.scheduledStartAt;
}

/** The last instant a proposed time may start: 6 days after the original. */
export function lastStart(original: string): number {
  return Date.parse(original) + 6 * DAY;
}

/** The day strip: today to the last day, in the coach's zone, every day. */
export function requestDays(nowMs: number, original: string, tz: string, anyTime = false): string[] {
  // DEV ONLY: LIVE_DEV_ANY_TIME. Every day from today on (the strip shows two weeks), no 6-day window.
  // Calendar days, not 24-hour steps: a clock change inside the fortnight would drop a day.
  if (anyTime) {
    const today = dayKey(new Date(nowMs).toISOString(), tz);
    return Array.from({ length: DEV_ANY_TIME_DAYS }, (_, i) => new Date(Date.parse(`${today}T12:00:00Z`) + i * DAY).toISOString().slice(0, 10));
  }
  return windowDays(new Date(nowMs).toISOString(), new Date(lastStart(original)).toISOString(), tz);
}

/** DEV ONLY: LIVE_DEV_ANY_TIME. How many days the any-time sheets show, from today. */
export const DEV_ANY_TIME_DAYS = 14;
/** DEV ONLY: LIVE_DEV_ANY_TIME. A drawn time's id: not an open time, so it is sent as its start (booking-contracts.md 10). */
export const DEV_ANY_TIME_ID = 'dev_any_time:';
/** DEV ONLY: LIVE_DEV_ANY_TIME. The start to send for a drawn time, or null for a real open time. */
export function devAnyTimeStart(slot: { id: string; startAt: string }): string | null {
  return slot.id.startsWith(DEV_ANY_TIME_ID) ? slot.startAt : null;
}

/**
 * The grid's step: the server's when it says (bookingWindow.slotMinutes), else read off the coach's
 * open times (they start on it: LIVE_SLOT_MINUTES, 10 on staging), else the half hour, which is on every grid.
 */
export function gridMinutes(reported: number | null | undefined, openStarts: string[]): number {
  if (reported && STEPS.includes(reported)) return reported;
  const gcd = (a: number, b: number): number => (b ? gcd(b, a % b) : a);
  let step = 30;
  for (const s of openStarts) step = gcd(step, new Date(s).getUTCMinutes() % 30);
  return STEPS.includes(step) ? step : 30;
}

export type TimeState = 'open' | 'past' | 'lead' | 'booked' | 'current' | 'late';

export interface RequestTime { startAt: string; state: TimeState }

/** One day's times, 6:00 AM to 11:00 PM on the grid, each with whether it can be sent. */
export function dayTimes(p: {
  day: string; tz: string; step: number; nowMs: number; leadMinutes: number; original: string;
  /** The lesson's own time, or '' for a screen that books a new one. */
  current: string; minutes: number; busy: Array<{ startAt: string; minutes: number }>;
  /** DEV ONLY: LIVE_DEV_ANY_TIME. Only the past and a clash are refused: no lead time, no last day. */
  anyTime?: boolean;
}): RequestTime[] {
  const out: RequestTime[] = [];
  const last = lastStart(p.original);
  for (let m = 6 * 60; m <= 23 * 60; m += p.step) {
    const hhmm = `${String(Math.floor(m / 60)).padStart(2, '0')}:${String(m % 60).padStart(2, '0')}`;
    const t = zonedInstant(p.day, hhmm, p.tz);
    // A day the clocks change can land a local time on the next day; it belongs to that day's list.
    if (dayKey(new Date(t).toISOString(), p.tz) !== p.day) continue;
    const startAt = new Date(t).toISOString();
    const clash = p.busy.some((x) => t < Date.parse(x.startAt) + x.minutes * MIN && Date.parse(x.startAt) < t + p.minutes * MIN);
    const state: TimeState = t <= p.nowMs ? 'past'
      : !p.anyTime && t - p.leadMinutes * MIN <= p.nowMs ? 'lead'
        : !p.anyTime && t > last ? 'late'
          : p.current && startAt === new Date(p.current).toISOString() ? 'current'
            : clash ? 'booked' : 'open';
    out.push({ startAt, state });
  }
  return out;
}

/** Add or remove a pick; never more than three. */
export function togglePick(picks: string[], startAt: string): string[] {
  if (picks.includes(startAt)) return picks.filter((x) => x !== startAt);
  return picks.length >= LESSON_TIMES_MAX ? picks : [...picks, startAt].sort();
}

/** "Request New Time", "Request 2 Times", "Request 3 Times". */
export function requestButtonLabel(n: number): string {
  return n >= 2 ? `Request ${n} Times` : 'Request New Time';
}

/** "Suggest up to three times. Ana picks one or keeps Wed, Nov 4." */
export function requestLine(playerFirst: string, current: string, tz: string): string {
  const day = new Date(current).toLocaleDateString('en-US', { weekday: 'short', month: 'short', day: 'numeric', timeZone: tz });
  return `Suggest up to three times. ${playerFirst} picks one or keeps ${day}.`;
}

export type LessonProposalStatus = 'open' | 'accepted' | 'kept' | 'picking' | 'replaced' | 'moved';

/** The thread card's closing line, by who reads it; null while it is open for the player. */
export function lessonProposalLine(p: {
  status: LessonProposalStatus; chosenLabel?: string | null; keptDay: string; isCoach: boolean; playerFirst: string;
}): string | null {
  switch (p.status) {
    case 'accepted': return p.isCoach ? `${p.playerFirst} picked ${p.chosenLabel ?? 'one of these times'}.` : `You picked ${p.chosenLabel ?? 'one of these times'}.`;
    case 'kept': return p.isCoach ? `${p.playerFirst} kept ${p.keptDay}.` : `You kept ${p.keptDay}.`;
    case 'picking': return p.isCoach ? `${p.playerFirst} is picking another time.` : 'You picked another time.';
    case 'replaced': return 'Newer times replaced these.';
    case 'moved': return 'The lesson has moved since.';
    default: return p.isCoach ? `Waiting for ${p.playerFirst}` : null;
  }
}

/**
 * The lesson a "moved the lesson" thread line is about (decision 123): its bookingId when the line has one,
 * else the one whose player_move was written at the line's moment (the server writes both from one clock).
 */
export function movedLessonOf<B extends { id: string; rescheduleHistory?: Array<{ via?: string; at?: string }> | null }>(
  lessons: B[], line: { bookingId?: string | null; createdAt?: string },
): B | undefined {
  if (line.bookingId) return lessons.find((b) => b.id === line.bookingId);
  const at = Date.parse(line.createdAt ?? '');
  if (Number.isNaN(at)) return undefined;
  return lessons.find((b) => (b.rescheduleHistory ?? []).some((h) => h.via === 'player_move' && Math.abs(Date.parse(h.at ?? '') - at) < 5_000));
}
